import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { PageHeader, Card, GhostButton, PrimaryButton, DataTable, Field, Input, Select, Textarea, DemoBadge } from "@/components/Primitives";
import StatusPill from "@/components/StatusPill";
import BidDecisionModal from "@/components/BidDecisionModal";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const isManualDownload = (v) =>
  v === true || (typeof v === "string" && ["yes", "true", "required", "1"].includes(v.trim().toLowerCase()));

const docsSourceLabel = (o) => {
  if (o.official_tender_link) return o.source_portal === "TED" ? "Official TED Notice" : "Official Source";
  if (o.source_portal) return `${o.source_portal} (no link)`;
  return "No source";
};

const SECTIONS = [
  { id: "summary", label: "Command Summary" },
  { id: "dates", label: "Critical Dates" },
  { id: "qual", label: "Bid Qualification" },
  { id: "reqs", label: "Requirements" },
  { id: "docs", label: "Documents & Source" },
  { id: "tasks", label: "Tasks" },
  { id: "suppliers", label: "Supplier Assignments" },
  { id: "samples", label: "Samples" },
  { id: "delivs", label: "Deliverables" },
  { id: "prod", label: "Production" },
  { id: "questions", label: "Questions" },
  { id: "notes", label: "Notes" },
];

export default function OpportunityDossier() {
  const { id } = useParams();
  const nav = useNavigate();
  const [opp, setOpp] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [linked, setLinked] = useState({ tasks: [], docs: [], assigns: [], samples: [], delivs: [], prod: [], reqs: [], questions: [] });
  const [decisionOpen, setDecisionOpen] = useState(false);
  const [logs, setLogs] = useState([]);
  const { can, user } = useAuth();
  const canEdit = can("opportunities", "update");
  const canDelete = can("opportunities", "delete");
  const canDecide = ["owner", "admin", "opportunity_manager", "tender_analyst"].includes(user?.role);

  const load = () => {
    api.get(`/collections/opportunities/${id}`).then((r) => { setOpp(r.data); setForm(r.data); });
    api.get(`/opportunities/${id}/decision-logs`).then((r) => setLogs(r.data)).catch(() => setLogs([]));
    Promise.all([
      api.get("/collections/tasks"),
      api.get("/collections/documents"),
      api.get("/collections/opportunity_suppliers"),
      api.get("/collections/supplier_samples"),
      api.get("/collections/supplier_deliverables"),
      api.get("/collections/supplier_production"),
      api.get("/collections/requirements"),
      api.get("/collections/supplier_questions"),
    ]).then(([a, b, c, d, e, f, g, h]) => {
      setLinked({
        tasks: a.data.filter((x) => x.opportunity_id === id),
        docs: b.data.filter((x) => x.opportunity_id === id),
        assigns: c.data.filter((x) => x.opportunity_id === id),
        samples: d.data.filter((x) => x.opportunity_id === id),
        delivs: e.data.filter((x) => x.opportunity_id === id),
        prod: f.data.filter((x) => x.opportunity_id === id),
        reqs: g.data.filter((x) => x.opportunity_id === id),
        questions: h.data.filter((x) => x.opportunity_id === id),
      });
    });
  };
  useEffect(load, [id]);

  const save = async () => {
    try {
      await api.patch(`/collections/opportunities/${id}`, { data: form });
      toast.success("Saved");
      setEditing(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const del = async () => {
    if (!window.confirm("Delete this opportunity? This cannot be undone.")) return;
    try {
      await api.delete(`/collections/opportunities/${id}`);
      toast.success("Deleted");
      nav("/opportunities");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  if (!opp) return <div className="p-8 text-zinc-500 text-xs tracking-widest uppercase">Loading dossier…</div>;

  return (
    <div>
      <PageHeader
        overline={`Dossier · ${opp.opportunity_type || "Opportunity"}`}
        title={<span>{opp.opportunity_name}<DemoBadge show={opp.is_demo} /></span>}
        sub={`${opp.country || ""} · ${opp.product_category || ""} · ${opp.division || ""}`}
        actions={
          <>
            <StatusPill value={opp.status} size="md" />
            {editing ? (
              <>
                <GhostButton onClick={() => { setForm(opp); setEditing(false); }} data-testid="dossier-cancel-btn">Cancel</GhostButton>
                <PrimaryButton onClick={save} data-testid="dossier-save-btn">Save</PrimaryButton>
              </>
            ) : (
              <>
                {canDelete && <GhostButton onClick={del} data-testid="dossier-delete-btn">Delete</GhostButton>}
                {canEdit && <PrimaryButton onClick={() => setEditing(true)} data-testid="dossier-edit-btn">Edit</PrimaryButton>}
              </>
            )}
          </>
        }
      />

      <div className="px-8 pt-6">
        <DecisionCard opp={opp} latestLog={logs[0]} canDecide={canDecide} onRecord={() => setDecisionOpen(true)} />
      </div>

      <div className="grid grid-cols-12 gap-6 p-8 pt-6">
        {/* Sticky left column */}
        <aside className="col-span-12 lg:col-span-3 lg:sticky lg:top-6 lg:self-start space-y-4">
          <Card className="p-4">
            <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-500 mb-2">Fit Score</div>
            <div className="font-heading font-bold text-5xl text-[#FFB300] tracking-tighter tabular">
              {opp.fit_score ?? "—"}
            </div>
            <div className="text-[10px] text-zinc-500 mt-1 tracking-widest uppercase">/ 100</div>
            <div className="mt-4 grid grid-cols-2 gap-2 text-[11px]">
              <Meta label="Deadline" value={opp.deadline} />
              <Meta label="Q&A" value={opp.qa_deadline} />
              <Meta label="Value" value={opp.estimated_value ? `${opp.currency || ""} ${opp.estimated_value}` : null} />
              <Meta label="Qty" value={opp.quantity} />
              <Meta label="Taiwan" value={opp.taiwan_eligibility} />
              <Meta label="Samples" value={opp.samples_required} />
              <Meta label="Bond" value={opp.bond_required} />
              <Meta label="Partner" value={opp.local_partner_needed} />
            </div>
          </Card>
          <Card className="p-3 text-[11px]">
            <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-500 mb-2">Jump to</div>
            <nav className="space-y-1">
              {SECTIONS.map((s) => (
                <a key={s.id} href={`#${s.id}`} className="block text-zinc-400 hover:text-[#FFB300] py-0.5">{s.label}</a>
              ))}
            </nav>
          </Card>
        </aside>

        <div className="col-span-12 lg:col-span-9 space-y-6">
          <Section id="summary" title="Command Summary">
            {editing ? (
              <Textarea rows={4} value={form.executive_summary || ""} onChange={(e) => setForm({ ...form, executive_summary: e.target.value })} />
            ) : (
              <p className="text-sm text-zinc-300 leading-relaxed">{opp.executive_summary || <em className="text-zinc-600">No summary.</em>}</p>
            )}
          </Section>

          <Section id="dates" title="Critical Dates & Status">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <EditableField editing={editing} label="Status" value={form.status} onChange={(v) => setForm({ ...form, status: v })} options={["New","Needs Review","Qualified","Documents Downloaded","Questions Needed","Bid/No-Bid Pending","Preparing Bid","Submitted","Award Watch","Won","Lost","No-Bid","Archived"]} display={<StatusPill value={opp.status} />} />
              <EditableField editing={editing} label="Deadline" value={form.deadline} onChange={(v) => setForm({ ...form, deadline: v })} type="date" />
              <EditableField editing={editing} label="Q&A Deadline" value={form.qa_deadline} onChange={(v) => setForm({ ...form, qa_deadline: v })} type="date" />
              <EditableField editing={editing} label="Next Action Due" value={form.next_action_due_date} onChange={(v) => setForm({ ...form, next_action_due_date: v })} type="date" />
              <EditableField editing={editing} label="Time Available" value={form.time_available} onChange={(v) => setForm({ ...form, time_available: v })} options={["30+ days","14-29 days","7-13 days","Less than 7 days","Expired"]} />
              <EditableField editing={editing} label="Submission Difficulty" value={form.submission_difficulty} onChange={(v) => setForm({ ...form, submission_difficulty: v })} options={["Low","Medium","High","Very High"]} />
              <EditableField editing={editing} label="Value Attractiveness" value={form.value_attractiveness} onChange={(v) => setForm({ ...form, value_attractiveness: v })} options={["High","Medium","Low"]} />
            </div>
          </Section>

          <Section id="qual" title="Bid Qualification">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <EditableField editing={editing} label="Product Match" value={form.product_match} onChange={(v) => setForm({ ...form, product_match: v })} options={["Strong","Medium","Weak","No Match"]} display={<StatusPill value={opp.product_match} />} />
              <EditableField editing={editing} label="Standards Match" value={form.standards_match} onChange={(v) => setForm({ ...form, standards_match: v })} options={["Exact Match","Likely Match","Needs Clarification","Mismatch"]} />
              <EditableField editing={editing} label="Taiwan Eligibility" value={form.taiwan_eligibility} onChange={(v) => setForm({ ...form, taiwan_eligibility: v })} options={["Yes","Likely","Unclear","Likely No","No"]} display={<StatusPill value={opp.taiwan_eligibility} />} />
              <EditableField editing={editing} label="Samples Required" value={form.samples_required} onChange={(v) => setForm({ ...form, samples_required: v })} options={["Yes","No","Unclear"]} />
              <EditableField editing={editing} label="Bond Required" value={form.bond_required} onChange={(v) => setForm({ ...form, bond_required: v })} options={["Yes","No","Unclear"]} />
              <EditableField editing={editing} label="Local Partner Needed" value={form.local_partner_needed} onChange={(v) => setForm({ ...form, local_partner_needed: v })} options={["Yes","No","Unclear"]} />
              <EditableField editing={editing} label="Fit Score" value={form.fit_score} onChange={(v) => setForm({ ...form, fit_score: Number(v) })} type="number" />
              <EditableField editing={editing} label="Supplier Assignment" value={form.supplier_assignment_status} onChange={(v) => setForm({ ...form, supplier_assignment_status: v })} options={["Not Assigned","Supplier Review","Samples Requested","Samples Received","Supplier Approved","Production Tracking","Closed"]} />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
              <Field label="Key Requirements">{editing ? <Textarea rows={3} value={form.key_requirements || ""} onChange={(e) => setForm({ ...form, key_requirements: e.target.value })}/> : <Pre>{opp.key_requirements}</Pre>}</Field>
              <Field label="Risks">{editing ? <Textarea rows={3} value={form.risks || ""} onChange={(e) => setForm({ ...form, risks: e.target.value })}/> : <Pre>{opp.risks}</Pre>}</Field>
            </div>
          </Section>

          <Section id="reqs" title={`Requirements (${linked.reqs.length})`}>
            <LinkedTable rows={linked.reqs} cols={[{ key: "requirement_id_label", label: "ID" }, { key: "requirement_type", label: "Type" }, { key: "required_standards", label: "Standards" }, { key: "supplier_visible", label: "Supplier Visible", render: (r) => <StatusPill value={r.supplier_visible ? "Yes" : "No"} /> }]} />
          </Section>

          <Section id="docs" title={`Documents (${linked.docs.length}) · ${docsSourceLabel(opp)}`}>
            {(opp.official_tender_link || opp.source_portal) && (
              <div className="mb-3 space-y-1 text-xs" data-testid="dossier-source-block">
                {opp.official_tender_link ? (
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-zinc-400">{opp.source_portal === "TED" ? "Official TED Notice" : "Official Source"}:</span>
                    <a href={opp.official_tender_link} target="_blank" rel="noreferrer" data-testid="dossier-official-source-link" className="text-[#FFB300] underline break-all">{opp.official_tender_link}</a>
                  </div>
                ) : (
                  <div className="text-zinc-500" data-testid="dossier-source-no-link">{opp.source_portal} notice — direct link not provided.</div>
                )}
                {opp.tender_id_notice_id && <div className="text-zinc-500">Notice ID: <span className="text-zinc-300 font-mono-data">{opp.tender_id_notice_id}</span></div>}
                {isManualDownload(opp.manual_download_required) && (
                  <span data-testid="dossier-manual-download" className="inline-block text-[10px] uppercase tracking-wide text-amber-300 border border-amber-300/40 px-2 py-0.5 mt-1">Manual Download Required</span>
                )}
              </div>
            )}
            <LinkedTable rows={linked.docs} cols={[{ key: "document_name", label: "Document" }, { key: "document_type", label: "Type" }, { key: "document_status", label: "Status", render: (r) => <StatusPill value={r.document_status} /> }, { key: "supplier_visible", label: "Supplier", render: (r) => r.supplier_visible ? "Yes" : "—" }, { key: "_open", label: "", render: (r) => (!r.storage_path && (r.source_url || r.official_tender_link)) ? <a href={r.source_url || r.official_tender_link} target="_blank" rel="noreferrer" data-testid={`dossier-doc-open-${r.id}`} className="text-[#FFB300] underline text-[11px]">{r.source_portal === "TED" ? "Open TED Notice" : "Open Source"}</a> : null }]} />
          </Section>

          <Section id="tasks" title={`Tasks (${linked.tasks.length})`}>
            <LinkedTable rows={linked.tasks} cols={[{ key: "task_name", label: "Task" }, { key: "due_date", label: "Due" }, { key: "priority", label: "Priority", render: (r) => <StatusPill value={r.priority} /> }, { key: "status", label: "Status", render: (r) => <StatusPill value={r.status} /> }]} />
          </Section>

          <Section id="suppliers" title={`Supplier Assignments (${linked.assigns.length})`}>
            <LinkedTable rows={linked.assigns} cols={[{ key: "assignment_name", label: "Assignment" }, { key: "supplier_id", label: "Supplier ID", mono: true }, { key: "assignment_status", label: "Status", render: (r) => <StatusPill value={r.assignment_status} /> }, { key: "sample_due_date", label: "Sample Due" }]} />
          </Section>

          <Section id="samples" title={`Samples (${linked.samples.length})`}>
            <LinkedTable rows={linked.samples} cols={[{ key: "sample_name", label: "Sample" }, { key: "quantity_requested", label: "Qty" }, { key: "sample_status", label: "Status", render: (r) => <StatusPill value={r.sample_status} /> }, { key: "testing_status", label: "Testing", render: (r) => <StatusPill value={r.testing_status} /> }]} />
          </Section>

          <Section id="delivs" title={`Deliverables (${linked.delivs.length})`}>
            <LinkedTable rows={linked.delivs} cols={[{ key: "deliverable_name", label: "Name" }, { key: "deliverable_type", label: "Type" }, { key: "due_date", label: "Due" }, { key: "status", label: "Status", render: (r) => <StatusPill value={r.status} /> }]} />
          </Section>

          <Section id="prod" title={`Production (${linked.prod.length})`}>
            <LinkedTable rows={linked.prod} cols={[{ key: "production_tracker_name", label: "Tracker" }, { key: "production_status", label: "Status", render: (r) => <StatusPill value={r.production_status} /> }, { key: "production_quantity", label: "Qty" }, { key: "planned_completion_date", label: "Planned" }]} />
          </Section>

          <Section id="questions" title={`Questions (${linked.questions.length})`}>
            <LinkedTable rows={linked.questions} cols={[{ key: "question_title", label: "Question" }, { key: "question_status", label: "Status", render: (r) => <StatusPill value={r.question_status} /> }, { key: "asked_date", label: "Asked" }]} />
          </Section>

          <Section id="notes" title="Notes">
            {editing ? <Textarea rows={4} value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} /> : <Pre>{opp.notes}</Pre>}
          </Section>
        </div>
      </div>

      {decisionOpen && (
        <BidDecisionModal
          open={decisionOpen}
          opportunity={opp}
          onClose={() => setDecisionOpen(false)}
          onSaved={(data) => { setOpp(data.opportunity); setForm(data.opportunity); load(); }}
        />
      )}
    </div>
  );
}

function Meta({ label, value }) {
  return (
    <div>
      <div className="text-zinc-500 text-[9px] tracking-widest uppercase">{label}</div>
      <div className="text-zinc-200 font-mono-data text-[11px] mt-0.5">{value ?? "—"}</div>
    </div>
  );
}
function Section({ id, title, children }) {
  return (
    <section id={id} className="scroll-mt-20">
      <div className="flex items-baseline justify-between border-b border-white/10 pb-1.5 mb-3">
        <h2 className="font-heading text-sm uppercase tracking-[0.2em] text-white">{title}</h2>
      </div>
      {children}
    </section>
  );
}
function Pre({ children }) {
  return children ? <pre className="whitespace-pre-wrap text-xs text-zinc-300 font-mono-data leading-relaxed">{children}</pre> : <span className="text-zinc-600 text-xs italic">empty</span>;
}
function EditableField({ editing, label, value, onChange, options, type, display }) {
  return (
    <Field label={label}>
      {editing ? (
        options ? (
          <Select value={value || ""} onChange={(e) => onChange(e.target.value)}>
            <option value="">—</option>
            {options.map((o) => <option key={o} value={o}>{o}</option>)}
          </Select>
        ) : (
          <Input type={type || "text"} value={value ?? ""} onChange={(e) => onChange(e.target.value)} />
        )
      ) : (
        display || <div className="text-zinc-200 text-xs font-mono-data">{value ?? "—"}</div>
      )}
    </Field>
  );
}
function LinkedTable({ rows, cols }) {
  if (!rows || rows.length === 0) return <div className="text-zinc-600 text-xs italic mt-2">No records.</div>;
  return <DataTable columns={cols} rows={rows} />;
}

function DecisionCard({ opp, latestLog, canDecide, onRecord }) {
  const status = opp.bid_decision_status || "Decision Needed";
  const reasons = opp.bid_decision_reasons || [];
  const isHold = status === "Hold / Awaiting Info";
  const decided = opp.bid_decision_at;
  const fmt = (s) => {
    if (!s) return "—";
    try { return new Date(s).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); }
    catch { return s; }
  };
  return (
    <Card className="p-4 border-l-2 border-[#FFB300]/40" data-testid="bid-decision-card">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-500">Bid / No-Bid Gate</div>
          <StatusPill value={status} size="md" />
        </div>
        {canDecide && (
          <PrimaryButton onClick={onRecord} data-testid="record-decision-btn">
            {opp.bid_decision_at ? "Update Decision" : "Record Decision"}
          </PrimaryButton>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-3">
        <div className="md:col-span-2">
          <div className="text-zinc-500 text-[9px] tracking-widest uppercase mb-1">Rationale</div>
          <div className="text-xs text-zinc-300 leading-relaxed" data-testid="bid-decision-rationale-summary">
            {opp.bid_decision_rationale || <span className="text-zinc-600 italic">No rationale recorded.</span>}
          </div>
          {reasons.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-2" data-testid="bid-decision-reasons-chips">
              {reasons.map((r) => (
                <span key={r} className="text-[10px] font-mono-data uppercase tracking-wider border border-white/10 text-zinc-400 px-2 py-0.5">{r}</span>
              ))}
            </div>
          )}
        </div>
        <div className="space-y-2 text-[11px]">
          <Meta label="Decided By" value={opp.bid_decision_by_email} />
          <Meta label="Decided At" value={decided ? fmt(decided) : null} />
          {isHold && <Meta label="Next Action" value={opp.bid_decision_next_action} />}
          {isHold && <Meta label="Next Action Due" value={opp.bid_decision_next_action_due_date} />}
        </div>
      </div>

      {latestLog && (
        <div className="mt-3 pt-3 border-t border-white/10 text-[10px] text-zinc-500 font-mono-data" data-testid="bid-decision-latest-log">
          Latest change: {latestLog.previous_decision_status} → {latestLog.new_decision_status} · {latestLog.decided_by_email} · {fmt(latestLog.decided_at)}
        </div>
      )}
    </Card>
  );
}
